import { Head, OriginProps } from 'next/document';
import React from 'react';

export type ExcludableCspSource =
  | 'https://maps.googleapis.com'
  | 'https://maps.gstatic.com'
  | 'https://js-agent.newrelic.com'
  | 'https://wasm.regulaforensics.com';

export type THeadProps = {
  unsafeHashes?: string[];
  excludeSources?: ExcludableCspSource[];
} & OriginProps &
  React.HTMLAttributes<HTMLHeadElement>;

export default function HeadWithContentSecurityPolicy({
  unsafeHashes,
  excludeSources,
  children,
  ...props
}: THeadProps) {
  const unsafeHashesString = unsafeHashes?.length
    ? unsafeHashes.map((hash) => `'${hash}'`).join(' ')
    : '';

  const staticLogoUrl = process.env.NEXT_PUBLIC_STATIC_LOGO_URL || '';

  // NEXT_PUBLIC_QRCODE_URL_PREFIX (see cdk/passwordless-auth/bin/env-configs.ts's
  // qrCodeGeneratorUrlPrefix) is a full URL prefix with path and query stub
  // (e.g. 'https://qr-code.dev.verifime.com/qr?url='), not a bare origin like
  // NEXT_PUBLIC_STATIC_LOGO_URL - a CSP source-list entry is matched by scheme+host+port, so only
  // the origin belongs in img-src. An unset var (e.g. this package's own unit tests, which don't
  // load any app's .env) is expected and silent. A *set but unparseable* value is config drift -
  // silently dropping it would reproduce the exact bug this block fixes (QR image blocked by
  // img-src) with nothing to grep for, so it is surfaced loudly in development, mirroring
  // FormAddressFields.tsx's handling of the same class of failure.
  const qrCodePrefix = process.env.NEXT_PUBLIC_QRCODE_URL_PREFIX;
  let qrCodeOrigin = '';
  if (qrCodePrefix) {
    try {
      qrCodeOrigin = new URL(qrCodePrefix).origin;
    } catch {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(
          `[HeadWithContentSecurityPolicy] invalid NEXT_PUBLIC_QRCODE_URL_PREFIX: ${qrCodePrefix}`,
        );
      }
    }
  }

  const exclude = new Set<string>(excludeSources ?? []);
  const keep = (source: string) => !exclude.has(source);

  const imgSources = [
    "'self'",
    'https://maps.gstatic.com',
    staticLogoUrl,
    qrCodeOrigin,
    'data:',
    'blob:',
  ]
    .filter(Boolean)
    .filter(keep);

  // 'unsafe-inline' is NOT required. NextScript (pages router, Next 14.2.x) only
  // emits __NEXT_DATA__ (type="application/json", not executed) and external bundles
  // (src="/_next/..."). The only executable inline script is the NR agent config in
  // each portal's _document.tsx, allowed via hash in unsafeHashes below.
  const scriptSources = [
    "'self'",
    "'wasm-unsafe-eval'",
    // React Refresh (Next.js HMR) uses eval() in development.
    // https://github.com/vercel/next.js/issues/14221
    process.env.NODE_ENV === 'development' ? "'unsafe-eval'" : '',
    'https://js-agent.newrelic.com',
    'https://maps.googleapis.com',
    unsafeHashesString,
  ]
    .filter(Boolean)
    .filter(keep);

  const workerSources = [
    "'self'",
    'https://wasm.regulaforensics.com',
    'blob:',
  ].filter(keep);

  const cspHeader = `
    object-src 'none';
    img-src ${imgSources.join(' ')};
    script-src ${scriptSources.join(' ')};
    base-uri 'none';
    form-action 'self';
    worker-src ${workerSources.join(' ')};
`;

  return (
    <Head {...props}>
      <meta httpEquiv="Content-Security-Policy" content={cspHeader} />
      {children}
    </Head>
  );
}
